import { Iterable as ImmutableIterable, List, Map } from 'immutable';
import { createSelector } from 'reselect';

import { ActiveLineData } from 'flow/types/ActiveLineData';
import { CallRecord } from 'flow/types/CallRecord';
import { ChatSession } from 'flow/types/Chats';
import {
    ActiveCallKey,
    CallRecordKey,
    ChatSessionKey,
    ContactKey,
    ExtensionKey,
    QueueKey,
} from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import { SipSessionWrapper } from 'flow/types/Phone';
import Queue from 'flow/types/Queue';
import { State } from 'flow/types/State';
import { selectPositionIsUnavailable } from 'flow/selectors/geolocation-selectors';
import { selectCallingNeedsCompanion } from 'flow/selectors/companion-selectors';
import { createTypedMap, TypedMap } from 'flow/types/TypedMap';
import { PbxSharedVoiceMessage } from 'flow/types/VoiceMessage';
import { getVideoConferenceWithSip } from 'flow/utils/browser-utils';
import {
    extractCallContact,
    extractCallRecordIndex,
    getEarliestConnectedCallRecordTimestampInMilliseconds,
} from 'flow/utils/call-records-utils';
import { getCallRecordChatSessionId } from 'flow/utils/call-record-utils';
import {
    CallState,
    getNumberOfOtherPerson,
    isRegisteredSIP,
    NewCallBlockReason,
} from 'flow/utils/call-utils';
import { getFirstActiveCallWithDirection } from 'flow/utils/prioritized-active-call-utils';
import { rtcSessionRepresentsTicketNonVoice } from 'flow/utils/ticket-utils';
import {
    selectExtensionKeyFromContactKey,
    selectExtensions,
    selectOwnExtensionKey,
} from './ExtensionSelectors';

import {
    selectActiveMeetingCallId,
    selectMeetingIsActive,
} from './meeting-selectors';
import { selectChatName } from './chat-selectors';
import { selectKeysWaitingQueueItems } from './queue-selectors';

import {
    getCallWaitingEnabled,
    getLoggedInUserCustomerKey,
    getOwnExtension,
    getOwnExtensionKey,
    hasMobileExtension,
    hasTeamsIntegrationExtension,
} from './selectors';

import {
    selectCallRecordFromKey,
    selectOwnCallRecordsActive,
    selectOwnCallRecordsNextSince,
    selectOwnCallRecordsNotActive,
} from './call-records-selectors';

// Re-export the normalized call-records selectors so existing call sites that
// still import from this file keep working. The canonical home is
// ./call-records-selectors; this re-export is dropped once all callers move.
export * from './call-records-selectors';

// This method is to be used when checking a session in state.extensions -> activeLineData,
// NOT to be used when checking a session in state.phone -> sessions
const sessionIsNonCallTelavoxTicket = (activeCall: TypedMap<ActiveLineData>) =>
    !!(
        activeCall.get('isTelavoxTicketNonCall') ||
        activeCall.getIn(['bagVariables', 'telavox.omni.ticket'])
    );

export const getActiveCalls = (state: State, extensionKey: ExtensionKey) => {
    const activeMeetingCallId = selectActiveMeetingCallId(state);
    const shouldIncludeCentionErrands =
        TREAT_CENTION_ERRANDS_LIKE_PHONE_CALLS_EXPERIMENTAL_FEATURE(state);

    if (shouldIncludeCentionErrands) {
        return state.extensions
            .getIn(['activeLineData', extensionKey], List())
            .filter(
                (item) =>
                    !sessionIsNonCallTelavoxTicket(item) &&
                    item.get('callId') !== activeMeetingCallId,
            );
    } else {
        return state.extensions
            .getIn(['activeLineData', extensionKey], List())
            .filter(
                (item) =>
                    !item.get('isOmnichannelErrand') &&
                    !sessionIsNonCallTelavoxTicket(item) &&
                    item.get('callId') !== activeMeetingCallId,
            );
    }
};

export const getOwnActiveCalls = (state: State) => {
    const ownExtensionKey = getOwnExtensionKey(state);
    return getActiveCalls(state, ownExtensionKey) || List();
};

export const selectOwnActiveCallsCount = (state: State) =>
    getOwnActiveCalls(state).size;

export const getOwnActiveCallByCallId = (state: State, callId: string) => {
    const activeCalls = getOwnActiveCalls(state);
    return activeCalls?.find((call) => call.get('callId') === callId);
};

export const getEarliestConnectedCallRecordStillActiveTimestampInMilliseconds =
    createSelector(
        [selectOwnCallRecordsActive, selectOwnCallRecordsNextSince],
        (callRecordsActive, nextSince) =>
            getEarliestConnectedCallRecordTimestampInMilliseconds(
                callRecordsActive,
            ) || nextSince,
    );

export const getRingingCall = (state: State) => {
    const extensionToCall = state.app.getIn(['callDialog', 'extensionToCall']);
    if (!extensionToCall) {
        return Map();
    }

    const extensionKey = extensionToCall.get('key');
    const activeCalls = getActiveCalls(state, extensionKey);
    const ringingCall = activeCalls.find(
        (call) => call.get('state') === 'ringing',
    );

    return ringingCall || Map();
};

/**
 * For video conference call records, returns the name of the linked chat session:
 * - For 1-on-1 video calls: the other person's display name
 * - For group video conferences: the chat room name
 * Returns undefined for non-video calls or when the session isn't loaded yet.
 */
export const selectCallRecordChatSessionDisplayName = (
    state: State,
    callRecordKey: CallRecordKey | undefined,
) => {
    if (!callRecordKey) return undefined;
    const callRecord = selectCallRecordFromKey(state, callRecordKey);
    const chatSessionId = getCallRecordChatSessionId(callRecord);
    if (chatSessionId === undefined) return undefined;
    return selectChatName(state, chatSessionId);
};

/**
 * For video conference call records, returns the linked chat session so that
 * avatar/room imagery can be rendered. Returns undefined for non-video calls
 * or when the session isn't loaded yet.
 */
export const selectCallRecordChatSession = (
    state: State,
    callRecordKey: CallRecordKey | undefined,
): TypedMap<ChatSession> | undefined => {
    if (!callRecordKey) return undefined;
    const callRecord = selectCallRecordFromKey(state, callRecordKey);
    const chatSessionId = getCallRecordChatSessionId(callRecord);
    if (chatSessionId === undefined) return undefined;
    return state.chats.getIn([
        'sessions',
        `chatsession-${chatSessionId}` as ChatSessionKey,
    ]);
};

export const filterCallRecordsBySearchableInfo = (
    callFilter = '',
    callRecords: ImmutableIterable<CallRecordKey, TypedMap<CallRecord>>,
) => {
    const searchString = callFilter?.toLowerCase() || '';
    return callRecords.filter(
        (callRecord) =>
            extractCallRecordIndex(callRecord).indexOf(searchString) !== -1,
    );
};

const extractVoicemessageIndex = (
    voicemessage: TypedMap<PbxSharedVoiceMessage>,
) => {
    const contact = voicemessage.get('contact');

    return (
        extractCallContact(contact as unknown as Map<string, string>) +
        ' ' +
        voicemessage.getIn(['phoneNumber', 'nationalFormat']) +
        ' ' +
        voicemessage.getIn(['phoneNumber', 'number']) +
        ' ' +
        voicemessage.getIn(['phoneNumber', 'prettyNationalNumber'])
    );
};

const filterVoiceMessagesBySearchableInfo = (
    voiceMessages: Map<string, TypedMap<PbxSharedVoiceMessage>>,
    callFilter: string | undefined = '',
) => {
    const searchString = callFilter ? callFilter.toLowerCase() : '';
    return voiceMessages.filter(
        (voiceMessage) =>
            extractVoicemessageIndex(voiceMessage).indexOf(searchString) !== -1,
    );
};

export const getCallListSearchFilter = (state: State) =>
    state.extensions.get('callListFilter', '');

export const getFilteredCallRecordsHistory = createSelector(
    [getCallListSearchFilter, selectOwnCallRecordsNotActive],
    (callFilter, callRecords) =>
        filterCallRecordsBySearchableInfo(callFilter, callRecords),
);

const getVoiceMessagesFetchedFromVoicemessagesApi = createSelector(
    [getOwnExtension],
    (ownExtension) => ownExtension.get('voiceMessages', Map()),
);

export const getFilteredVoicemessagesFetchedFromVoicemailsApi = createSelector(
    [getCallListSearchFilter, getVoiceMessagesFetchedFromVoicemessagesApi],
    (callFilter, voiceMessages) =>
        filterVoiceMessagesBySearchableInfo(voiceMessages, callFilter),
);

export const showCommunicationCanvas = (state: State) => {
    const numLocalCalls = getNumLocalCalls(state);
    const activeCalls = getOwnActiveCalls(state);
    const activeCall = getFirstActiveCallWithDirection(activeCalls);

    const meetingIsActive = selectMeetingIsActive(state);

    return numLocalCalls !== 0 || !!activeCall || meetingIsActive;
};

const localSessionIsTelavoxTicketNonCall = (
    sessionStoreMap: TypedMap<SipSessionWrapper>,
) =>
    sessionStoreMap.get('isTelavoxTicketNonCall') ||
    rtcSessionRepresentsTicketNonVoice(sessionStoreMap.get('session'));

export const getLocalCallSessionsWrapperData = createSelector(
    [(state: State) => state.phone.get('sessions')],
    (sessionsData) =>
        sessionsData.filter(
            (data) => !localSessionIsTelavoxTicketNonCall(data),
        ),
);

export const getLocalCallIds = createSelector(
    [getLocalCallSessionsWrapperData],
    (callSessionsData) => callSessionsData.keySeq().toArray(),
);

export const getLocalActiveCalls = createSelector(
    [getLocalCallIds, getOwnActiveCalls],
    (localCallIds, ownActiveCalls) =>
        ownActiveCalls.filter(
            (activeCall) =>
                activeCall.get('callId') &&
                localCallIds.includes(activeCall.get('callId')),
        ),
);

export const getLocalCallSessions = createSelector(
    [getLocalCallSessionsWrapperData],
    (callSessionsData) =>
        callSessionsData.toList().map((obj) => obj.get('session')),
);

export const getNumLocalCalls = createSelector(
    [getLocalCallSessionsWrapperData],
    (callSessionsData) => callSessionsData.size,
);

/**
 * Whether this client holds a softphone call. Only these carry the microphone
 * we open, so only these are affected by the local audio pipeline; a call on a
 * desk phone or a mobile never reaches it.
 */
export const selectLocalCallIsOngoing = (state: State) =>
    getNumLocalCalls(state) > 0;

export const getPaiHeaderFromLocalCall = (state: State) =>
    state.phone.get('pai');

export const getLocalSessionFromCallId = (state: State, callId: string) =>
    state.phone.getIn(['sessions', callId, 'session']);

export const isSessionOnHold = (state: State, callId: string) =>
    state.phone.getIn(['sessions', callId, 'isOnHold'], false);

export const isSessionEstablished = (state: State, callId: string) =>
    state.phone.getIn(['sessions', callId, 'isEstablished'], false);

// TODO: Replace meeting-selectors.ts:_getIsPhoneConnected once this file is Typescripted
export const getIsPhoneConnected = (state: State) =>
    state.phone.get('isConnected');

export const isLocalFocusedCallSessionEstablished = (state: State) =>
    isSessionEstablished(state, getFocusedLocalCallId(state));

export const getFocusedActiveCall = (state: State) => {
    const focusedCallId = getFocusedLocalCallId(state);
    if (focusedCallId) {
        return getOwnActiveCallByCallId(state, focusedCallId);
    }

    const activeCalls = getOwnActiveCalls(state);
    return getFirstActiveCallWithDirection(activeCalls);
};

// Find if the logged-in user is in a call with state 'UP' or 'PARKED'.
// It's important that the call is 'UP' or 'PARKED'; if it's 'RINGING' or something else,
// we shouldn't present the 'transfer call' button.
export const selectLoggedInUserIsInCall = createSelector(
    [getFocusedActiveCall],
    (activeCall) => {
        if (!activeCall) {
            return false;
        }
        return (
            CallState.UP.is(activeCall.get('state')) ||
            CallState.PARKED.is(activeCall.get('state'))
        );
    },
);

export const getFocusedLocalCallId = createSelector(
    [
        getLocalCallSessionsWrapperData,
        (state: State) => state.meeting.getIn(['activeMeeting', 'meetingCode']),
    ],
    (callSessionsData = createTypedMap(), activeMeetingCode?: string) => {
        const sessions = callSessionsData ?? Map();
        if (sessions.size === 0 && !activeMeetingCode) {
            return undefined;
        }
        if (sessions.size === 1 && !activeMeetingCode) {
            return sessions.keySeq().first();
        }
        const focusedLocalCall = sessions.findKey((object, callId) => {
            if (
                object.get('session').direction === 'incoming' &&
                !object.get('isEstablished')
            ) {
                return true;
            }
            const otherIncomingCallNotAnswered = sessions.some(
                (otherObject, otherCallId) => {
                    if (callId === otherCallId) {
                        return false;
                    }
                    return (
                        otherObject.get('session').direction === 'incoming' &&
                        !otherObject.get('isEstablished')
                    );
                },
            );
            if (otherIncomingCallNotAnswered) {
                return false;
            }
            return !object.get('isOnHold');
        });
        if (focusedLocalCall) {
            return focusedLocalCall;
        } else {
            return activeMeetingCode;
            // Comment from Max who's on git blame here:
            // I have my suspicion that what should be returned here is the value of:
            // state.meeting.getIn(['activeMeeting', 'call', 'callId'])
            // instead of:
            // state.meeting.getIn(['activeMeeting', 'meetingCode']
            // What I've done here is simply modifying this whole method, getFocusedLocalCallId, to
            // being a memoized selector instead of a plain selector, and as I can't say for sure,
            // I've left it as is, i.e. returning the value of state.meeting.getIn(['activeMeeting', 'meetingCode']
        }
    },
);

export const getFocusedSession = (state: State) =>
    getLocalSessionFromCallId(state, getFocusedLocalCallId(state));

export const getDisplayMode = (state: State) => state.app.get('callCanvasSize');

export const canSwapSessions = (state: State, callId: string) => {
    const callSessions = getLocalCallSessionsWrapperData(state) || Map();
    const numberOfSessions = selectMeetingIsActive(state)
        ? callSessions.size + 1
        : callSessions.size;

    if (numberOfSessions !== 2) {
        return false;
    }
    return callSessions.every((_sessionMap, key: CallRecordKey) => {
        if (key === callId) {
            if (!isSessionOnHold(state, key)) {
                return false;
            }
        } else {
            const holdable =
                isSessionEstablished(state, key) &&
                !isSessionOnHold(state, key);
            if (!holdable) {
                return false;
            }
        }
        return true;
    });
};

export const canMerge = (state: State, callId: string) => {
    const focusedLocalCallId = getFocusedLocalCallId(state);
    const numberOfLocalCalls = getNumLocalCalls(state);
    return focusedLocalCallId === callId && numberOfLocalCalls > 1;
};

export const getPhoneIsMuted = (state: State) => state.phone.get('isMuted');

/**
 * The TOP channel name for one of the user's own calls, or null when it isn't known yet.
 * ActiveLineData carries no channel, so it comes from the raw NewChannel message.
 */
export const getChannelNameForCallId = (state: State, callId: string) => {
    const activeCall = getOwnActiveCallByCallId(state, callId);
    if (!activeCall) {
        return null;
    }

    const uniqueId = activeCall.get('key', '').replace('activecall-', '');
    if (!uniqueId) {
        return null;
    }

    return state.top.getIn(['uniqueIdToChannel', uniqueId, 'Channel']) ?? null;
};

export const hasActiveCallsBlockingNewCall = (
    state: State,
    direction: string,
    maxCalls = 1,
) => {
    const localCallIds = getLocalCallIds(state);
    const meetingActive = selectMeetingIsActive(state);
    const handleMeetingAsPhoneCall = getVideoConferenceWithSip();

    if (selectCallingNeedsCompanion(state)) {
        return NewCallBlockReason.COMPANION_REQUIRED;
    }

    if (selectPositionIsUnavailable(state)) {
        return NewCallBlockReason.GEO_LOCATION_BLOCKED;
    }

    if (hasPendingAttendedTransfer(state)) {
        return NewCallBlockReason.TRANSFER_IN_PROGRESS;
    }

    if (isCoListening(state)) {
        return NewCallBlockReason.CO_LISTENING_IN_PROGRESS;
    }

    if (
        direction === 'incoming' &&
        !getCallWaitingEnabled(state) &&
        (localCallIds.length > 0 || (meetingActive && handleMeetingAsPhoneCall))
    ) {
        return NewCallBlockReason.CALL_WAITING_OFF_TOO_MANY_CALLS;
    }

    const numberOfActiveCalls = meetingActive
        ? localCallIds.length + 1
        : localCallIds.length;
    if (numberOfActiveCalls >= maxCalls) {
        return NewCallBlockReason.TOO_MANY_CALLS;
    }

    if (numberOfActiveCalls > 0) {
        const sessionsData = getLocalCallSessionsWrapperData(state);
        const otherCallsEstablished = sessionsData.every((sessionData) =>
            sessionData.get('isEstablished'),
        );
        if (!otherCallsEstablished) {
            return NewCallBlockReason.CALLS_NOT_ESTABLISHED;
        }
    }

    return NewCallBlockReason.NONE;
};

export const getImmutableVerifiedCaller = createSelector(
    [(verifiedCaller) => verifiedCaller],
    (verifiedCaller) => Map(verifiedCaller),
);

export const getAgentInfo = (state: State, callId: string) => {
    const agentInfoOnSession = state.phone.getIn([
        'sessions',
        callId,
        'agentInfo',
    ]);
    if (agentInfoOnSession) {
        return agentInfoOnSession;
    }

    const activeCall = getOwnActiveCallByCallId(state, callId);
    const verifiedCaller =
        activeCall && activeCall.getIn(['bagVariables', 'verified-caller']);
    return verifiedCaller ? getImmutableVerifiedCaller(verifiedCaller) : null;
};

export const allowToggleCall = (state: State) => {
    const numLocalCalls = getNumLocalCalls(state);
    const hasActiveCall = getOwnActiveCalls(state).size > 0;
    const localSession = getFocusedSession(state);
    const sessionIsIncoming =
        localSession && localSession.direction === 'incoming';
    const sessionIsEstablished = isLocalFocusedCallSessionEstablished(state);
    const hasMobile = hasMobileExtension(state);
    const hasTeamsIntegration = hasTeamsIntegrationExtension(state);

    if (numLocalCalls > 1) {
        return false;
    }

    if (!hasActiveCall && !localSession) {
        return false;
    }

    if (localSession && sessionIsIncoming && !sessionIsEstablished) {
        return false;
    }

    const hasLocalCall = numLocalCalls > 0;
    const extension = getOwnExtension(state);
    const isRegistered =
        isRegisteredSIP(extension) || state.phone.get('isRegistered');

    return hasLocalCall
        ? hasMobile || hasTeamsIntegration || isRegisteredSIP(extension)
        : isRegistered;
};

export const selectActiveCallOfContact = (
    state: State,
    contactKey: ContactKey,
) => {
    const extensionKey = selectExtensionKeyFromContactKey(state, contactKey);
    const activeCalls = getActiveCalls(state, extensionKey);
    return getFirstActiveCallWithDirection(activeCalls);
};

/**
 * When co-listening: get the number of the remote party who the subject is talking to.
 */
export const getCoListeningRemotePartyNumber = (
    state: State,
    subjectContactKey: ContactKey,
) => {
    const activeCall = selectActiveCallOfContact(state, subjectContactKey);

    return activeCall && getNumberOfOtherPerson(activeCall);
};

export const isCoListening = (state: State) =>
    getLocalCallSessions(state).some(
        (session) =>
            session.direction === 'outgoing' &&
            session.remote_identity.uri.user.startsWith('*28*'),
    );

export const makeSelectCoListenerContactsByCallId = () =>
    createSelector(
        [selectExtensions, getOwnActiveCallByCallId],
        (extensions, activeCall) => {
            if (!activeCall) {
                return null;
            }

            return activeCall
                .get('colisteners', Map())
                .map((extensionKey) =>
                    extensions.getIn([extensionKey, 'contact']),
                )
                .filter((contact) => !!contact)
                .toList();
        },
    );

export const isExtensionInCall = (state: State, extensionKey: ExtensionKey) => {
    const activeCalls = getActiveCalls(state, extensionKey);
    if (!activeCalls) {
        return false;
    }
    return activeCalls.some((activeCall) => activeCall.get('state') === 'up');
};

export const hasPendingAttendedTransfer = (state: State) =>
    !!getActiveCallWithPendingTransfer(state);

export const getPendingTransferNumber = (state: State) => {
    const activeCallWithPendingTransfer =
        getActiveCallWithPendingTransfer(state);
    return (
        activeCallWithPendingTransfer &&
        activeCallWithPendingTransfer.getIn(['pendingTransfer', 'number'])
    );
};

export const getPendingTransferStartTime = (state: State) => {
    const activeCallWithPendingTransfer =
        getActiveCallWithPendingTransfer(state);
    return (
        activeCallWithPendingTransfer &&
        activeCallWithPendingTransfer.getIn(['pendingTransfer', 'startTime'])
    );
};

export const getPendingTransferCallKey = (state: State) => {
    const activeCallWithPendingTransfer =
        getActiveCallWithPendingTransfer(state);
    return (
        activeCallWithPendingTransfer &&
        activeCallWithPendingTransfer.getIn(['pendingTransfer', 'callKey'])
    );
};

const getActiveCallWithPendingTransfer = (state: State) => {
    const activeCalls = getOwnActiveCalls(state);
    return activeCalls.find((activeCall) => activeCall.has('pendingTransfer'));
};

export const TREAT_CENTION_ERRANDS_LIKE_PHONE_CALLS_EXPERIMENTAL_FEATURE = (
    state: State,
) => {
    const loggedInUserCustomerKey = getLoggedInUserCustomerKey(state);
    return (
        loggedInUserCustomerKey === 'customer-228532' || //Strwb
        loggedInUserCustomerKey === 'customer-43' || //Tvx
        loggedInUserCustomerKey === 'customer-210056' || //Helix test
        loggedInUserCustomerKey === 'customer-278867' //VD
    );
};

export const SHOULD_DELAY_REMOVAL_OF_SESSION_OF_TYPE_CENTION_ERRAND = (
    state: State,
) => TREAT_CENTION_ERRANDS_LIKE_PHONE_CALLS_EXPERIMENTAL_FEATURE(state);

export const selectNumberOfWaitingQueueItems = (
    state: State,
    queues: TypedMap<Record<QueueKey, TypedMap<Queue>>>,
) => {
    if (!queues) {
        return 0;
    }
    let numberOfQueueItems = 0;
    queues.forEach((queue) => {
        const itemKeys = selectKeysWaitingQueueItems(state, queue.get('key'));
        numberOfQueueItems = numberOfQueueItems + itemKeys.size;
    });

    return numberOfQueueItems;
};

export const isRegistered = (state: State, extension: TypedMap<Extension>) =>
    isRegisteredSIP(extension) || state.phone.get('isRegistered');

export const selectOwnActiveLineData = (state: State) =>
    state.extensions.getIn(['activeLineData', selectOwnExtensionKey(state)]);

export const selectOwnActiveLineDataWithKey = createSelector(
    [
        selectOwnActiveLineData,
        (_state: State, activeCallKey: ActiveCallKey) => activeCallKey,
    ],
    (activeLineData, activeCallKey) =>
        activeLineData?.find((call) => call.get('key') === activeCallKey),
);

export const selectOwnActiveLineLocutusSessionId = (
    state: State,
    activeCallKey: ActiveCallKey,
) =>
    selectOwnActiveLineDataWithKey(state, activeCallKey)?.get(
        'locutusSessionId',
    );

export const selectOwnActiveLineCallState = (
    state: State,
    activeCallKey: ActiveCallKey,
) => selectOwnActiveLineDataWithKey(state, activeCallKey)?.get('state');

export const selectSoftphoneIsRegistered = (state: State) =>
    state.phone.get('isRegistered');

export const selectOwnActiveLineQueueKey = (
    state: State,
    activeCallKey: ActiveCallKey,
) => selectOwnActiveLineDataWithKey(state, activeCallKey)?.get('queue');

export const selectOwnActiveLineDirection = (
    state: State,
    activeCallKey: ActiveCallKey,
) => selectOwnActiveLineDataWithKey(state, activeCallKey)?.get('direction');
